import React, { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';

export default function ProjectDetail({ project, onClose }) {
  const rootRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const q = gsap.utils.selector(root);
    const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
    tl.fromTo(root, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.7 })
      .fromTo(q('.detail-index'), { yPercent: 120 }, { yPercent: 0, duration: 0.6 }, '-=0.35')
      .fromTo(q('.detail-title span'), { yPercent: 120 }, { yPercent: 0, duration: 0.8, stagger: 0.05 }, '<')
      .fromTo(
        q('.detail-meta, .detail-desc, .detail-tags, .detail-actions'),
        { y: 28, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.08 },
        '-=0.5'
      )
      .fromTo(
        q('.detail-media'),
        { scale: 0.85, opacity: 0, rotate: 3 },
        { scale: 1, opacity: 1, rotate: 0, duration: 0.9, ease: 'expo.out' },
        '-=0.7'
      )
      .fromTo(q('.detail-close'), { scale: 0, rotate: -90 }, { scale: 1, rotate: 0, duration: 0.4 }, '-=0.6');
    tlRef.current = tl;
    return () => tl.kill();
  }, [project]);

  const handleClose = useCallback(() => {
    const root = rootRef.current;
    gsap.to(root, {
      clipPath: 'inset(0% 0% 100% 0%)',
      duration: 0.55,
      ease: 'power4.inOut',
      onComplete: onClose,
    });
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && handleClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  return (
    <div className="detail" ref={rootRef} style={{ '--accent': project.accent }}>
      <div className="detail-bg" />
      <button className="detail-close" onClick={handleClose} aria-label="Close project">
        ✕
      </button>

      <div className="detail-layout">
        <div className="detail-copy">
          <div className="mask">
            <span className="detail-index">{project.index} / 06</span>
          </div>
          <h2 className="detail-title" aria-label={project.title}>
            {project.title.split(' ').map((w, i) => (
              <span className="mask" key={i}>
                <span>{w}</span>
              </span>
            ))}
          </h2>
          <p className="detail-meta">
            <span className={`detail-status ${project.status}`}>
              {project.status === 'live' ? '● Live' : '◦ Archived'}
            </span>
            <span className="detail-tagline">{project.tagline}</span>
          </p>
          <p className="detail-desc">{project.description}</p>
          <ul className="detail-tags">
            {project.tags.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          <div className="detail-actions">
            {project.url ? (
              <a className="btn-primary" href={project.url} target="_blank" rel="noreferrer">
                Visit live site <span aria-hidden>↗</span>
              </a>
            ) : (
              <span className="btn-ghost">No longer online</span>
            )}
            <button className="btn-back" onClick={handleClose}>
              ← All apps
            </button>
          </div>
        </div>

        <div className="detail-media">
          <div className="detail-media-frame">
            <img src={project.gif} alt={`${project.title} demo`} draggable="false" />
          </div>
          <img className="detail-logo" src={project.logo} alt="" aria-hidden draggable="false" />
        </div>
      </div>
    </div>
  );
}
